import { useGetAllAddressesQuery } from "@/api/AppApi/services/address";
import { useEffect, useRef, useState } from "react";
import { Col, Row } from "@/utils/bootstrap";
import { Controller, FieldValues, UseFormReturn } from "react-hook-form";
import SvgEdit from "../../checkout-icons/SvgEdit";
import { ScrollPosition, useScroll } from "@/hooks/useScroll";
import InputErrorMsg from "@/app/components/common-form-builder/form-builder-helpers/InputErrorMsg";
import ManageAddress from "../../../user-pages/address-book/ManageAddress";
import { useTranslation } from "@/hooks/localization/useTranslations";
import GeneralApiLoader from "@/app/components/lib/loaders/GeneralApiLoader";
import { useGetProfileQuery } from "@/api/AppApi/services/auth";
import BaseOption from "@/app/components/common-filter-builder/common-filters-components/base-checkbox/BaseOption";
import CheckoutBoxHeader from "../../checkout-helpers/CheckoutBoxHeader";
import PopUp from "@/app/components/common-builder/pop-ups-components/pop-up-helpers/PopUp";
import SvgAddressBook from "@/app/layouts/header/header-common/common-header-icons/SvgAddressBook";
import PageDrawer from "@/app/components/lib/modal-as-page/PageDrawer";
import SvgCloseModal from "../../../cart/all-cart-items/cart-item/manage-cart-item/SvgCloseModal";

interface Props {
  form: UseFormReturn<FieldValues, any>;
  fieldName: string;
  mobile: boolean;
  setAreaId?: (areaId: string | number) => void;
  setCityId?: (cityId: string | number) => void;
  setCountryId?: (countryId: string | number) => void;
  className?: string;
}

const UserCheckoutAddresses: React.FC<Props> = ({
  mobile,
  form,
  fieldName,
  setAreaId,
  setCityId,
  setCountryId,
  className,
}) => {
  const t = useTranslation();
  form.register(fieldName, { required: t.this_field_is_required });
  const [addressIoEdit, setAddressIoEdit] = useState<number | null>(null);
  const [show, setShow] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const [, setScrollPosition] = useState<ScrollPosition>({ x: 0, y: 0 });
  const getProfile = useGetProfileQuery();
  const { delivery_address_obj: delivery_address } =
    getProfile?.data?.result?.settings ?? {};
  const getAllAddressess = useGetAllAddressesQuery({ page: 1, limit: 24 });
  const addresses =
    getAllAddressess.data?.pages?.map((page) => page.result.data).flat() ?? [];
  const selectedId = form.watch(fieldName);
  useEffect(() => {
    if (getAllAddressess.isLoading || getProfile.isLoading) return;
    const defaultAddressId = (
      addresses?.find((address) => address.is_default) ?? addresses[0]
    )?.id;
    const deliveryAddressId = delivery_address;
    setTimeout(() =>
      form.setValue(fieldName, deliveryAddressId ?? defaultAddressId),
    );
    setAddressIoEdit(defaultAddressId);
    return () => form.unregister(fieldName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAllAddressess.isLoading, getProfile.isLoading]);

  const selectedAddress = addresses?.find(
    (address) => address.id === selectedId,
  );
  useEffect(() => {
    setAreaId && setAreaId(selectedAddress?.area_id ?? 0);
    setCityId && setCityId(selectedAddress?.city_id ?? 0);
    setCountryId && setCountryId(selectedAddress?.country_id ?? 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, selectedAddress]);
  const fetchMore = () => {
    if (getAllAddressess.hasNextPage) {
      getAllAddressess.fetchNextPage();
    }
  };
  useScroll(box, box?.current?.scrollHeight ?? 0, setScrollPosition, fetchMore);
  if (getAllAddressess?.isFetching) return <GeneralApiLoader />;
  return (
    <div className={className}>
      <CheckoutBoxHeader
        fontSize={18}
        title={t.address_book}
        mobile={mobile}
        customAction={
          <div
            className="link-btn"
            onClick={() => {
              setAddressIoEdit(null);
              setShow(true);
            }}
          >
            {t.add_new}
          </div>
        }
      />

      <div className="checkout-box-content">
        <>
          <div className="branches w-100" ref={box}>
            {addresses?.length === 0 ? (
              <div className="no-shipping-methods">
                <SvgAddressBook width={54} />
                <p className="m-0">{t.you_havent_add_any_addresses_yet}</p>
              </div>
            ) : (
              addresses?.map((address, key) => (
                <div
                  className={`branch p-3 ${
                    key !== addresses.length - 1 ? "border-bottom" : ""
                  } ${
                    selectedAddress?.id === address.id ? "selected-method" : ""
                  }`}
                  key={key}
                >
                  <Controller
                    name={fieldName}
                    control={form.control}
                    rules={{
                      required: {
                        value: true,
                        message: t.this_field_is_required,
                      },
                    }}
                    render={({ field: { value, onChange, ref } }) => (
                      <Row
                        className="m-0 name align-items-center justify-content-between"
                        style={{ gap: 4 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onChange(address?.id);
                        }}
                      >
                        <Col className="p-0 col-auto d-flex align-items-center ">
                          <BaseOption
                            label={""}
                            is_multi_select={false}
                            toggleOption={() => {}}
                            selected={value === address.id}
                            optionRef={ref}
                            value={value}
                          />
                          <div className="d-flex flex-column">
                            <div className="address ">
                              {`${address.address_name}`}
                            </div>
                            <div className="address primary">
                              {address.other_address ?? t.noAddress}
                            </div>
                          </div>
                        </Col>
                        <Col className="p-0 col-auto" title={t.edit}>
                          <SvgEdit
                            onClick={() => {
                              setAddressIoEdit(address.id);
                              setShow(true);
                            }}
                          />
                        </Col>
                      </Row>
                    )}
                  />
                </div>
              ))
            )}
          </div>
          {form.formState.errors[fieldName] && (
            <InputErrorMsg
              errorMsg={form.formState.errors[fieldName]?.message as string}
            />
          )}

          {show && (
            <>
              {mobile ? (
                <PageDrawer
                  padding="36px 16px 100px 16px"
                  title={addressIoEdit ? t.edit_address : t.add_new_address}
                  show={show}
                  setShow={() => setShow(false)}
                >
                  <ManageAddress
                    id={addressIoEdit}
                    onManageSuccess={() => setShow(false)}
                    onCancel={() => setShow(false)}
                  />
                </PageDrawer>
              ) : (
                <PopUp
                  isOpen={show}
                  onClose={() => setShow(false)}
                  contentClassName="scrollbar p-0"
                >
                  <div>
                    <div className=" d-flex align-items-center sticky-header p-4 justify-content-between">
                      <h5 className="m-0">
                        {addressIoEdit ? t.edit_address : t.add_new_address}
                      </h5>
                      <SvgCloseModal onClick={() => setShow(false)} />
                    </div>
                    <div className="p-4">
                      {" "}
                      <ManageAddress
                        id={addressIoEdit}
                        onManageSuccess={() => setShow(false)}
                        onCancel={() => setShow(false)}
                      />
                    </div>
                  </div>
                </PopUp>
              )}
            </>
          )}
        </>
      </div>
    </div>
  );
};

export default UserCheckoutAddresses;
